import { Directive, EventEmitter, Input, Output } from '@angular/core'
import { ErrorTier, PresentedError } from 'src/utils/presented-error'
import { dismissPanelError, isPanelErrorDismissed } from 'src/utils/dismissed-panel-errors'
import { SurfaceErrorHostDirective } from './surface-error-host.directive'

/**
 * Tier 3 of the error system: a state conflict renders on the application, job or resource it
 * is about, because that object is on screen and the condition outlives the click.
 *
 * Registry, lifetime and action rules are the dialog host's; only the tier differs. The call
 * site names it with `errorContext.objectId`, alongside `dialogId` when the submit came from a
 * dialog — the dialog declines an object-tier error and this claims it:
 *
 * ```html
 * <div arpObjectErrorHost="application-actions" #objectError="objectErrorHost">
 *   @if (objectError.error) {
 *     <arp-error-block [error]="objectError.error" (dismissed)="objectError.dismiss()"></arp-error-block>
 *   }
 * </div>
 * ```
 *
 * Put it on a region that outlives the request. A control that its own click unmounts — one
 * that switches tabs before subscribing — is never mounted to claim the response.
 */
@Directive({
  selector: '[arpObjectErrorHost]',
  exportAs: 'objectErrorHost',
  standalone: false,
})
export class ObjectErrorHostDirective extends SurfaceErrorHostDirective {
  /** Distinguishes hosts in `ErrorAnchorService.registeredIds` when debugging. */
  @Input('arpObjectErrorHost') override hostId = 'panel'

  /**
   * Fires when this host takes an error. A dialog that submitted the work uses it to stand
   * down: the condition belongs to the object behind it, and the dialog cannot resolve it.
   */
  @Output() claimed = new EventEmitter<PresentedError>()

  /**
   * Remembers a dismissal in localStorage until sign-out, for a condition the panel re-raises on
   * its own (page load, a recheck). A recurrence is claimed and dropped rather than toasted.
   */
  @Input() rememberDismissal = false

  protected override readonly acceptedTiers: readonly ErrorTier[] = ['object']

  dismiss(): void {
    if (this.rememberDismissal && this.error) dismissPanelError(this.hostId, this.error)
    this.clear()
  }

  protected override claim(presented: PresentedError): boolean {
    const taken = super.claim(presented)
    if (taken && this.rememberDismissal && isPanelErrorDismissed(this.hostId, presented)) {
      this.clear()
      return true
    }
    if (taken) this.claimed.emit(presented)
    return taken
  }
}
